import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SimilarTours = ({ tours }) => {
  const navigate = useNavigate();

  const handleTourClick = (tourId) => {
    navigate(`/tour-detail?id=${tourId}`);
    window.scrollTo(0, 0);
  };

  const handleViewAll = () => {
    navigate('/search-results?category=similar');
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={index < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="bg-white">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Similar Tours You Might Like</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewAll}
          iconName="ArrowRight"
          iconPosition="right"
        >
          View All
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tours?.map((tour) => (
          <div
            key={tour?.id}
            className="group bg-white border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={() => handleTourClick(tour?.id)}
          >
            {/* Tour Image */}
            <div className="relative h-48 overflow-hidden">
              <Image
                src={tour?.image}
                alt={tour?.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              
              {/* Badges */}
              <div className="absolute top-3 left-3 flex flex-col space-y-1">
                {tour?.isPopular && (
                  <span className="bg-accent text-white px-2 py-1 rounded-full text-xs font-medium">
                    Popular
                  </span>
                )}
                {tour?.discount && (
                  <span className="bg-success text-white px-2 py-1 rounded-full text-xs font-medium">
                    {tour?.discount}% OFF
                  </span>
                )}
              </div>

              {/* Wishlist Button */}
              <button className="absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors">
                <Icon name="Heart" size={16} className="text-muted-foreground hover:text-red-500" />
              </button>

              {/* Quick View */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Button
                  variant="secondary"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                >
                  Quick View
                </Button>
              </div>
            </div>

            {/* Tour Content */}
            <div className="p-4">
              {/* Category & Duration */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                  {tour?.category}
                </span>
                <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                  <Icon name="Clock" size={12} />
                  <span>{tour?.duration}</span>
                </div>
              </div>

              {/* Tour Name */}
              <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {tour?.name}
              </h3>

              {/* Location */}
              <div className="flex items-center space-x-1 text-sm text-muted-foreground mb-3">
                <Icon name="MapPin" size={14} />
                <span>{tour?.location}</span>
              </div>

              {/* Rating */}
              <div className="flex items-center space-x-2 mb-3">
                <div className="flex items-center">
                  {renderStars(tour?.rating)}
                </div>
                <span className="text-sm font-medium">{tour?.rating}</span>
                <span className="text-xs text-muted-foreground">({tour?.reviewCount})</span>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-1 mb-3">
                {tour?.features?.slice(0, 2)?.map((feature, index) => (
                  <span
                    key={index}
                    className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
                {tour?.features?.length > 2 && (
                  <span className="text-xs text-muted-foreground">
                    +{tour?.features?.length - 2} more
                  </span>
                )}
              </div>

              {/* Price */}
              <div className="flex items-center justify-between">
                <div className="flex items-baseline space-x-1">
                  <span className="text-lg font-bold text-foreground">${tour?.price}</span>
                  {tour?.originalPrice && (
                    <span className="text-sm text-muted-foreground line-through">
                      ${tour?.originalPrice}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">per person</div>
              </div>

              {/* Availability */}
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${
                      tour?.availability === 'high' ? 'bg-success' :
                      tour?.availability === 'medium' ? 'bg-warning' : 'bg-error'
                    }`} />
                    <span className="text-muted-foreground">
                      {tour?.availability === 'high' ? 'Good availability' :
                       tour?.availability === 'medium' ? 'Limited availability' : 'Few spots left'}
                    </span>
                  </div>
                  {tour?.instantBooking && (
                    <span className="text-success font-medium">Instant booking</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Comparison Tool */}
      <div className="mt-8 p-6 bg-muted/30 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground mb-1">Compare Tours</h3>
            <p className="text-sm text-muted-foreground">
              Select multiple tours to compare features, prices, and reviews
            </p>
          </div>
          <Button
            variant="outline"
            iconName="BarChart3"
            iconPosition="left"
          >
            Compare Selected
          </Button>
        </div>
      </div>
      {/* Why These Tours */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Why we recommend these tours</h4>
            <p className="text-sm text-blue-800">
              Based on similar destinations, activities, and positive reviews from travelers with similar preferences
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimilarTours;